// One-time account activation (RadioOS-managed authentication, documentation/protocol.md).
//
// The activation link is <origin>/activate#<token>. The token is a bearer secret:
//   - it travels in the URL FRAGMENT, which browsers never send to a server, proxy log or Referer;
//   - takeActivationToken removes it from the address bar immediately (history.replaceState), so
//     it is not left in the URL, history entry or a later copy/paste of the address;
//   - it is kept in memory only (component state) and sent only in POST bodies; it is never
//     written to localStorage, sessionStorage or a cookie.
// Activation never signs the user in: there is no session or cookie afterwards.

export type InspectResult = 'valid' | 'invalid' | 'rate_limited' | 'unavailable';

export type CompleteResult =
  | { kind: 'activated'; next: 'sign_in' | 'mfa_required' }
  | { kind: 'policy'; message: string }
  | { kind: 'invalid' }
  | { kind: 'rate_limited' }
  | { kind: 'unavailable' };

/** The v1 password policy as the server enforces it (pkg/managedauth/policy.go). */
export const PASSWORD_MIN_CHARS = 15;
export const PASSWORD_MAX_CHARS = 256;
export const PASSWORD_RULES = [
  `At least ${PASSWORD_MIN_CHARS} characters (up to ${PASSWORD_MAX_CHARS}). A passphrase of several words works well.`,
  'Any characters are allowed; no special mix of letters, digits or symbols is required.',
  'Not a commonly used password.',
  'Not based on your e-mail address or the product name.',
  'Not just one or two characters repeated.',
];

/** Length in characters as the server counts it (Unicode NFC code points, not bytes). */
export function passwordLength(password: string): number {
  return Array.from(password.normalize('NFC')).length;
}

const TOKEN_SHAPE = /^[A-Za-z0-9_-]{43}$/;

/**
 * Reads the activation token from the URL fragment and removes it from the address bar at once.
 * Returns '' when there is no well-formed token.
 */
export function takeActivationToken(
  loc: { hash: string; pathname: string },
  hist: { replaceState: (data: unknown, unused: string, url?: string) => void },
): string {
  const raw = loc.hash.startsWith('#') ? loc.hash.slice(1) : loc.hash;
  if (loc.hash) hist.replaceState(null, '', loc.pathname);
  return TOKEN_SHAPE.test(raw) ? raw : '';
}

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

const post = (fetcher: Fetcher, path: string, body: unknown) =>
  fetcher(path, {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

/** The two one-time-link flows: account activation and administrator-issued password reset. */
export type LinkPurpose = 'activation' | 'reset';
const ENDPOINT: Record<LinkPurpose, string> = { activation: '/api/auth/activation', reset: '/api/auth/password-reset' };

/** Checks a link without consuming it. */
export async function inspectActivation(token: string, fetcher: Fetcher = (i, n) => fetch(i, n), purpose: LinkPurpose = 'activation'): Promise<InspectResult> {
  if (!TOKEN_SHAPE.test(token)) return 'invalid';
  try {
    const res = await post(fetcher, ENDPOINT[purpose] + '/inspect', { token });
    if (res.ok) return 'valid';
    if (res.status === 410 || res.status === 404) return 'invalid';
    if (res.status === 429) return 'rate_limited';
    return 'unavailable';
  } catch {
    return 'unavailable';
  }
}

/** Sets the first password and consumes the link. */
export async function completeActivation(token: string, password: string, fetcher: Fetcher = (i, n) => fetch(i, n), purpose: LinkPurpose = 'activation'): Promise<CompleteResult> {
  try {
    const res = await post(fetcher, ENDPOINT[purpose] + '/complete', { token, password });
    if (res.ok) {
      const body = (await res.json()) as { next?: string };
      if (purpose === 'reset') return { kind: 'activated', next: 'sign_in' };
      return { kind: 'activated', next: body.next === 'mfa_required' ? 'mfa_required' : 'sign_in' };
    }
    if (res.status === 422) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      return { kind: 'policy', message: typeof body.message === 'string' ? body.message : 'The password does not meet the requirements.' };
    }
    if (res.status === 410 || res.status === 404) return { kind: 'invalid' };
    if (res.status === 429) return { kind: 'rate_limited' };
    return { kind: 'unavailable' };
  } catch {
    return { kind: 'unavailable' };
  }
}
